import React from 'react';
import userPersonas from '@common/constants/userPersonas';
import StringConstants from '@common/constants/StringConstants';
import Modal from '@common/components/ConstellationV10Modal';
import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Heading,
  List,
  Paragraph,
  Radio,
  TextButton,
} from '@zillow/constellation-10';

type Props = {
  onSubmit: () => void;
  selectedOption: string;
  setSelectedOption: (selectedOption: string) => void;
  onClose: () => void;
  onBack: () => void;
  emergencyMode: boolean;
};

const TaxTypeSelectorPage: React.FC<React.PropsWithChildren<Props>> = (props: Props): JSX.Element => {
  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={props.emergencyMode ? undefined : props.onClose}
      header={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          {!props.emergencyMode && (
            <TextButton textStyle="body-sm-bold" tone="brand" css={{ float: 'left' }} onClick={props.onBack}>
              Back
            </TextButton>
          )}
          <Heading level={3} textStyle="heading-xs" css={{ display: 'flex', justifyContent: 'center' }}>
            Payment setup
          </Heading>
        </Box>
      }
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'token(spacing.sm) !important',
            padding: 'token(spacing.lg) !important',
          }}
        >
          <Paragraph textStyle="body-lg-bold">Verify your tax information</Paragraph>
          <Paragraph>
            Verifying your tax status and identification number(s) will help ensure that payments you collect are
            correctly reported.
          </Paragraph>
          <Paragraph textStyle="body-bold">
            <Anchor
              css={{ textDecoration: 'none' }}
              href={StringConstants.LINKS.TAX_VERIFICATION_LEARN_MORE.LANDLORD}
              target="_blank"
            >
              Learn more
            </Anchor>
          </Paragraph>
          <List.Root>
            <List.Item>This information won&apos;t be shared with renters</List.Item>
            <List.Item>Zillow doesn&apos;t store this information</List.Item>
          </List.Root>
          <Paragraph textStyle="body-bold">
            For this rental&apos;s income, are you taxed as an individual or a business?
          </Paragraph>
          <Box css={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'token(spacing.sm)' }}>
            <Radio
              css={{ margin: '4px' }}
              onChange={() => props.setSelectedOption('individual')}
              checked={props.selectedOption === 'individual'}
              value="individual"
            />
            <Box>
              <Paragraph textStyle="body-bold">An individual</Paragraph>
              <Paragraph>
                You rent property that you own as an investment, and don&apos;t separate the income from any other
                personal income you have.
              </Paragraph>
            </Box>
          </Box>
          <Box css={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'token(spacing.sm)' }}>
            <Radio
              css={{ margin: '4px' }}
              onChange={() => props.setSelectedOption('company')}
              checked={props.selectedOption === 'company'}
              value="company"
            />
            <Box>
              <Paragraph textStyle="body-bold">A business</Paragraph>
              <Paragraph>
                Your rental property is owned by a separate business entity, and the rental income is kept separate from
                your personal income.
              </Paragraph>
            </Box>
          </Box>

          <Box>
            <Paragraph textStyle="fineprint">
              By clicking &quot;Continue&quot; you agree to comply with our third-party payment processor&apos;s
              <Anchor
                css={{ textDecoration: 'none' }}
                target="_blank"
                href="https://stripe.com/us/connect-account/legal"
              >
                &nbsp;Payment Terms
              </Anchor>
              &nbsp;and
              <Anchor css={{ textDecoration: 'none' }} target="_blank" href="https://plaid.com/legal">
                &nbsp;Bank Authorization Terms
              </Anchor>
              .
            </Paragraph>
          </Box>
        </Box>
      }
      footer={
        <ButtonGroup css={{ padding: 'token(spacing.sm) !important' }} aria-label={'Submit'}>
          <Button emphasis="filled" disabled={!props.selectedOption} onClick={props.onSubmit}>
            Continue
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default TaxTypeSelectorPage;
